import { RowSelectionState, SortingState } from "@tanstack/react-table";
import map from "lodash/map";
import pick from "lodash/pick";
import { useExperienceConfig } from "PFApp/hooks/use_experience_config";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { Id, ProfileCustomValue } from "PFCore/types";
import { Fragment, useMemo, useState } from "react";

import { groupSkillsByExperience } from "../../../charts/helpers";
import { useAccordionTrigger } from "../../hooks/use_accordion_trigger";
import css from "../../skills_section.module.scss";
import { SkillsViewCommonProps } from "../../skills_section.types";
import { SkillsEmptyState } from "../skills_empty_state";
import { SkillsGroupAccordion } from "../skills_group_accordion";

type SkillsByProficiencyViewProps = {
  skills: ProfileCustomValue[];
  profileId: Id;
} & SkillsViewCommonProps;

export const SkillsByProficiencyView = ({
  skills,
  profileId,
  rowSelectionState,
  searchTerm,
  setSearchTerm,
  ...props
}: SkillsByProficiencyViewProps) => {
  const { experienceLevels } = useExperienceConfig();
  const experienceLabelMap = useExperienceLabelMap();

  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
  const { stateTrigger, handleStateChange } = useAccordionTrigger(searchTerm);

  const skillsByExperience = useMemo(() => groupSkillsByExperience(skills), [skills]);
  const experienceLevelsToDisplay = useMemo(
    () =>
      [...experienceLevels]
        .reverse()
        .filter((experience) => (skillsByExperience[experience] || []).length > 0),
    [experienceLevels, skillsByExperience]
  );

  if (!experienceLevelsToDisplay.length) {
    return <SkillsEmptyState searchTerm={searchTerm} setSearchTerm={setSearchTerm} />;
  }

  return (
    <Stack gap="spacingLg">
      {experienceLevelsToDisplay.map((experience) => {
        const experienceSkills = skillsByExperience[experience] || [];

        const experienceRowSelectionState = pick(
          rowSelectionState,
          map(experienceSkills || [], "id")
        ) as RowSelectionState;

        return (
          <Fragment key={experience}>
            <SkillsGroupAccordion
              {...props}
              header={`${experienceLabelMap[experience]} (${experienceSkills.length})`}
              state={stateTrigger}
              onStateChange={handleStateChange}
              skills={experienceSkills}
              controlledSorting={sorting}
              onSortingChange={setSorting}
              profileId={profileId}
              rowSelectionState={experienceRowSelectionState}
            />
            <Divider className={css.groupDivider} />
          </Fragment>
        );
      })}
    </Stack>
  );
};
